import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuth } from '@/hooks/useAuth';
import { useAudio } from '@/hooks/useAudio';
import { api } from '@/services/api';
import { DiceRollModal } from '@/components/dice/DiceRollModal';
import { LastBreathModal } from '@/components/game/LastBreathModal';
import { InventoryModal } from '@/components/game/InventoryModal';
import { AbilitiesModal } from '@/components/game/AbilitiesModal';
import { parseRollRequest, removeRollMarkers, RollRequest } from '@/utils/dice';
import type { Character } from '@/types';
import type { MusicTrack } from '@/hooks/useAudio';

interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  senderType?: string;
  createdAt?: string;
}

interface GameSession {
  id: string;
  name: string | null;
  currentScene: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ChapterInfo {
  id: string;
  number: number;
  title: string | null;
  totalChapters: number;
}

interface SessionResponse {
  session: GameSession;
  chapter?: ChapterInfo;
  messages: Message[];
}

interface NewSessionResponse {
  session: GameSession;
  greeting: string;
}

interface MessageResponse {
  reply: string;
  sessionId: string;
  sceneData?: {
    scene?: string;
    xp_marked?: boolean;
    damage_taken?: number;
  };
  character?: Character;
  chapterClosed?: boolean;
  chapterInfo?: {
    closedChapter: { number: number; title: string | null };
    newChapter: { id: string; number: number };
  };
}

// Input states for two-step input
type InputState = 'NORMAL' | 'AWAITING_DESCRIPTION' | 'AWAITING_ROLL' | 'WAITING_AI';

// Helper to calculate stat modifier
function getModifier(stat: number): string {
  let mod = 0;
  if (stat <= 3) mod = -3;
  else if (stat <= 5) mod = -2;
  else if (stat <= 8) mod = -1;
  else if (stat <= 12) mod = 0;
  else if (stat <= 15) mod = 1;
  else if (stat <= 17) mod = 2;
  else mod = 3;
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

export function AdventurePage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const { character, refreshCharacter, setCharacter } = useAuth();
  const { isMusicPlaying, toggleMusic, playHitSound, changeTrack } = useAudio();

  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionName, setSessionName] = useState<string>('Приключение');
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirmEnd, setShowConfirmEnd] = useState(false);
  const [showConfirmRestart, setShowConfirmRestart] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);

  // Two-step input state
  const [inputState, setInputState] = useState<InputState>('NORMAL');
  const [pendingRoll, setPendingRoll] = useState<RollRequest | null>(null);
  const [pendingDescription, setPendingDescription] = useState<string>('');
  const [showDiceModal, setShowDiceModal] = useState(false);

  // Last Breath (death) state
  const [showLastBreathModal, setShowLastBreathModal] = useState(false);
  const [lastBreathProcessed, setLastBreathProcessed] = useState(false);

  // Inventory modal state
  const [showInventory, setShowInventory] = useState(false);

  // Abilities modal state
  const [showAbilities, setShowAbilities] = useState(false);

  // Chapter state
  const [currentChapter, setCurrentChapter] = useState<ChapterInfo | null>(null);
  const [showChapterToast, setShowChapterToast] = useState<string | null>(null);

  // Stats panel state
  const [showStats, setShowStats] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  // Scroll to the START of the last message when new GM message arrives
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        // Scroll to the START of the last GM message
        lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // For user messages, scroll to end
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  // Load session on mount
  useEffect(() => {
    loadSession();
  }, []);

  // Check for character death (HP = 0)
  useEffect(() => {
    if (
      character &&
      character.currentHp !== undefined &&
      character.currentHp <= 0 &&
      !lastBreathProcessed &&
      !showLastBreathModal
    ) {
      setShowLastBreathModal(true);
    }
  }, [character?.currentHp, lastBreathProcessed, showLastBreathModal]);

  const loadSession = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get<SessionResponse | null>('/game/session');

      if (response && response.session) {
        setSessionId(response.session.id);
        setSessionName(response.session.name || 'Приключение');

        // Set chapter info
        if (response.chapter) {
          setCurrentChapter(response.chapter);
        }

        // Process messages to check for pending rolls and clean markers
        const processedMessages = response.messages.map(m => ({
          ...m,
          content: removeRollMarkers(m.content).replace(/\[MUSIC:\w+\]/g, '').replace(/\[CHAPTER_END:[^\]]+\]/g, '').trim(),
        }));
        setMessages(processedMessages);

        // Check last GM message for roll request
        const lastGmMessage = response.messages.filter(m => m.role === 'assistant').pop();
        if (lastGmMessage) {
          const rollRequest = parseRollRequest(lastGmMessage.content);
          if (rollRequest) {
            setPendingRoll(rollRequest);
            setInputState('AWAITING_DESCRIPTION');
          }
        }
      } else {
        // No session - redirect to start page
        navigate('/adventure');
      }
    } catch (err) {
      console.error('Failed to load session:', err);
      setError('Не удалось загрузить сессию');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!sessionId) return;

    try {
      await api.put(`/game/session/${sessionId}/status`, { status: 'paused' });
      hapticFeedback('success');
      setShowMenu(false);
      setShowSaveToast(true);
      setTimeout(() => setShowSaveToast(false), 2000);
    } catch (err) {
      console.error('Failed to save session:', err);
      hapticFeedback('error');
    }
  };

  const handleExit = async () => {
    if (sessionId) {
      try {
        await api.put(`/game/session/${sessionId}/status`, { status: 'paused' });
      } catch (err) {
        console.error('Failed to save session:', err);
      }
    }
    hapticFeedback('light');
    navigate('/');
  };

  const handleEndAdventure = async () => {
    if (!sessionId) return;

    try {
      await api.put(`/game/session/${sessionId}/status`, { status: 'completed' });
      hapticFeedback('success');
      setShowConfirmEnd(false);
      navigate('/');
    } catch (err) {
      console.error('Failed to end session:', err);
      hapticFeedback('error');
    }
  };

  const handleStartOver = async () => {
    try {
      setShowConfirmRestart(false);
      setShowMenu(false);
      setIsLoading(true);

      const response = await api.post<NewSessionResponse>('/game/session/new', {});

      setSessionId(response.session.id);
      setSessionName(response.session.name || 'Приключение');
      setPendingRoll(null);
      setPendingDescription('');
      setInputState('NORMAL');

      // Check greeting for roll request and clean markers
      const rollRequest = parseRollRequest(response.greeting);
      let cleanGreeting = removeRollMarkers(response.greeting);
      cleanGreeting = cleanGreeting.replace(/\[MUSIC:\w+\]/g, '').replace(/\[CHAPTER_END:[^\]]+\]/g, '').trim();

      setMessages([{
        role: 'assistant',
        content: cleanGreeting,
      }]);

      if (rollRequest) {
        setPendingRoll(rollRequest);
        setInputState('AWAITING_DESCRIPTION');
      }

      hapticFeedback('success');
    } catch (err) {
      console.error('Failed to start new session:', err);
      setError('Не удалось начать новое приключение');
      hapticFeedback('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Send messages to AI (can send multiple messages at once)
  const sendToAI = async (messagesToSend: string[]) => {
    if (messagesToSend.length === 0 || isSending) return;

    setIsSending(true);
    setError(null);
    hapticFeedback('light');

    try {
      // Send combined message
      const combinedMessage = messagesToSend.join('\n\n');
      const response = await api.post<MessageResponse>('/game/message', {
        message: combinedMessage,
        sessionId,
      });

      // Check for music marker and apply
      const musicMatch = response.reply.match(/\[MUSIC:(\w+)\]/);
      if (musicMatch) {
        const track = musicMatch[1] as MusicTrack;
        if (['ambient', 'fight', 'relax', 'underground', 'victory'].includes(track)) {
          changeTrack(track);
        }
      }

      // Check for roll request in response
      const rollRequest = parseRollRequest(response.reply);
      // Remove both roll markers, music markers, and chapter markers
      let cleanReply = removeRollMarkers(response.reply);
      cleanReply = cleanReply.replace(/\[MUSIC:\w+\]/g, '').replace(/\[CHAPTER_END:[^\]]+\]/g, '').trim();

      // Add GM response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: cleanReply,
      }]);

      // Handle chapter closing
      if (response.chapterClosed && response.chapterInfo) {
        const title = response.chapterInfo.closedChapter.title || `Глава ${response.chapterInfo.closedChapter.number}`;
        setShowChapterToast(`Глава завершена: "${title}"`);
        setTimeout(() => setShowChapterToast(null), 4000);

        // Update current chapter info
        setCurrentChapter(prev => prev ? {
          ...prev,
          id: response.chapterInfo!.newChapter.id,
          number: response.chapterInfo!.newChapter.number,
          title: null,
          totalChapters: prev.totalChapters + 1,
        } : null);
      }

      if (rollRequest) {
        setPendingRoll(rollRequest);
        setInputState('AWAITING_DESCRIPTION');
      } else {
        setPendingRoll(null);
        setInputState('NORMAL');
      }

      if (response.sessionId && !sessionId) {
        setSessionId(response.sessionId);
      }

      // Update character data if returned from API
      if (response.character) {
        setCharacter(response.character);
      }

      // Play hit sound if damage was taken
      if (response.sceneData?.damage_taken) {
        playHitSound();
      }

      hapticFeedback('light');
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Не удалось отправить сообщение');
      hapticFeedback('error');
    } finally {
      setIsSending(false);
    }
  };

  // Handle sending description (step 1 of two-step input)
  const handleSendDescription = () => {
    const text = inputText.trim();
    if (!text) return;

    // Add description to local chat
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setPendingDescription(text);
    setInputText('');
    setInputState('AWAITING_ROLL');
    hapticFeedback('light');
  };

  // Handle normal message send (when no roll is pending)
  const handleSendNormal = async () => {
    const text = inputText.trim();
    if (!text || isSending) return;

    setInputText('');

    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: text }]);

    await sendToAI([text]);
  };

  // Handle dice roll result (step 2 of two-step input)
  const handleDiceResult = async (resultMessage: string) => {
    setShowDiceModal(false);
    // Note: dice sound is now played in DiceRollModal when clicking "Roll"

    // IMMEDIATELY deactivate dice and switch to waiting state
    setPendingRoll(null);
    setInputState('WAITING_AI');

    // Add roll result to local chat
    setMessages(prev => [...prev, { role: 'user', content: resultMessage }]);

    // Now send BOTH messages to AI
    const messagesToSend = pendingDescription
      ? [pendingDescription, resultMessage]
      : [resultMessage];

    // Reset pending description
    setPendingDescription('');

    await sendToAI(messagesToSend);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputState === 'AWAITING_DESCRIPTION') {
        handleSendDescription();
      } else if (inputState === 'NORMAL') {
        handleSendNormal();
      }
    }
  };

  const handleSendClick = () => {
    if (inputState === 'AWAITING_DESCRIPTION') {
      handleSendDescription();
    } else if (inputState === 'NORMAL') {
      handleSendNormal();
    }
  };

  // Handle Last Breath roll result
  const handleLastBreathResult = async (roll: number, outcome: 'survive' | 'bargain' | 'death') => {
    setLastBreathProcessed(true);
    setShowLastBreathModal(false);

    // Create message about the roll
    const rollMessage = `[Последний вздох: ${roll}]`;

    if (outcome === 'survive') {
      // Player survives - set HP to 1
      try {
        await api.post('/characters/heal', { hp: 1, setTo: true });
        await refreshCharacter();

        // Add message to chat and notify AI
        setMessages(prev => [...prev, {
          role: 'user',
          content: `${rollMessage} Я возвращаюсь к жизни, едва избежав смерти.`
        }]);

        await sendToAI([`${rollMessage} Персонаж прошёл проверку Последнего Вздоха (10+) и возвращается к жизни с 1 HP. Опиши, как он приходит в себя.`]);
      } catch (err) {
        console.error('Failed to heal character:', err);
      }
    } else if (outcome === 'bargain') {
      // Death offers a bargain
      setMessages(prev => [...prev, {
        role: 'user',
        content: `${rollMessage} Я стою на пороге между жизнью и смертью...`
      }]);

      await sendToAI([`${rollMessage} Персонаж получил 7-9 на проверке Последнего Вздоха. Смерть предлагает ему сделку. Опиши появление Смерти и её предложение - что она требует в обмен на жизнь персонажа? Это должно быть что-то значимое: душа друга, важное воспоминание, часть силы, обещание служить и т.д.`]);
    } else {
      // Character dies
      try {
        await api.post('/characters/die', {});
        await refreshCharacter();

        setMessages(prev => [...prev, {
          role: 'user',
          content: `${rollMessage} Тьма поглощает меня...`
        }]);

        await sendToAI([`${rollMessage} Персонаж провалил проверку Последнего Вздоха (6-) и умирает. Опиши его последние мгновения и уход в мир иной. Это конец его истории.`]);
      } catch (err) {
        console.error('Failed to mark character as dead:', err);
      }
    }
  };

  // Get placeholder text based on state
  const getPlaceholder = () => {
    if (inputState === 'AWAITING_DESCRIPTION') {
      return 'Опишите своё действие...';
    }
    if (inputState === 'AWAITING_ROLL') {
      return 'Бросьте кубик...';
    }
    if (inputState === 'WAITING_AI') {
      return 'Ожидание ответа...';
    }
    return 'Что вы делаете?';
  };

  // Check if input should be disabled
  const isInputDisabled = isLoading || isSending || inputState === 'AWAITING_ROLL' || inputState === 'WAITING_AI';

  return (
    <div className="h-screen bg-gray-900 flex flex-col safe-area-top safe-area-bottom">
      {/* Sticky Header */}
      <header className="sticky top-0 z-30 bg-gray-900 border-b border-gray-700 pt-safe">
        {/* Top row: navigation */}
        <div className="flex items-center justify-between p-2 px-3">
          <button
            onClick={() => {
              hapticFeedback('light');
              navigate('/adventure');
            }}
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            ← Назад
          </button>
          <div className="text-center truncate max-w-[180px]">
            <span className="font-bold text-white block">
              {character?.name || sessionName}
            </span>
            {currentChapter && (
              <span className="text-[10px] text-amber-400">
                Глава {currentChapter.number}{currentChapter.title ? `: ${currentChapter.title}` : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Music toggle button - always visible */}
            <button
              onClick={() => {
                hapticFeedback('light');
                toggleMusic();
              }}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <span className="text-lg">{isMusicPlaying ? '🔊' : '🔇'}</span>
            </button>
            {/* Menu button */}
            <button
              onClick={() => {
                hapticFeedback('light');
                setShowMenu(!showMenu);
              }}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
          </div>

          {/* Dropdown Menu */}
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full right-3 mt-1 w-52 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden"
              >
                <button
                  onClick={() => {
                    toggleMusic();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-3 text-left text-white hover:bg-gray-700 flex items-center gap-3"
                >
                  <span>{isMusicPlaying ? '🔊' : '🔇'}</span>
                  <span>{isMusicPlaying ? 'Выключить музыку' : 'Включить музыку'}</span>
                </button>
                <div className="h-px bg-gray-700" />
                <button
                  onClick={handleSave}
                  className="w-full px-4 py-3 text-left text-white hover:bg-gray-700 flex items-center gap-3"
                >
                  <span>💾</span>
                  <span>Сохранить</span>
                </button>
                <button
                  onClick={handleExit}
                  className="w-full px-4 py-3 text-left text-white hover:bg-gray-700 flex items-center gap-3"
                >
                  <span>🚪</span>
                  <span>Выйти</span>
                </button>
                <div className="h-px bg-gray-700" />
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowConfirmEnd(true);
                  }}
                  className="w-full px-4 py-3 text-left text-red-400 hover:bg-gray-700 flex items-center gap-3"
                >
                  <span>🏁</span>
                  <span>Завершить приключение</span>
                </button>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowConfirmRestart(true);
                  }}
                  className="w-full px-4 py-3 text-left text-amber-400 hover:bg-gray-700 flex items-center gap-3"
                >
                  <span>🔄</span>
                  <span>Начать заново</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* XP bar and character stats */}
        {character && (
          <>
            {/* XP Progress Bar */}
            <div className="px-3 pb-1">
              <div className="flex justify-between text-[10px] text-gray-400 mb-0.5">
                <span>Уровень {character.level ?? 1}</span>
                <span>{character.xp ?? 0} / {(character.level ?? 1) + 7} XP</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-1">
                <div
                  className="bg-purple-500 h-1 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, ((character.xp ?? 0) / ((character.level ?? 1) + 7)) * 100)}%` }}
                />
              </div>
            </div>

            {/* Stats row with icons */}
            <div className="flex items-center justify-between px-3 pb-2">
              {/* Left: HP, Fatigue, Armor, Coins */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* HP */}
                <div className="flex items-center gap-1 bg-red-900/30 px-2 py-1 rounded-lg">
                  <span className="text-sm">❤️</span>
                  <span className="text-white font-bold text-xs">
                    {character.currentHp ?? 0}/{character.maxHp ?? 0}
                  </span>
                </div>
                {/* Fatigue */}
                <div className="flex items-center gap-1 bg-purple-900/30 px-2 py-1 rounded-lg">
                  <span className="text-sm">💤</span>
                  <span className={`font-bold text-xs ${
                    (character.fatigue ?? 0) >= 7 ? 'text-red-400' : 'text-purple-400'
                  }`}>
                    {character.fatigue ?? 0}/{character.maxFatigue ?? 10}
                  </span>
                </div>
                {/* Armor */}
                <div className="flex items-center gap-1 bg-blue-900/30 px-2 py-1 rounded-lg">
                  <span className="text-sm">🛡️</span>
                  <span className="text-blue-400 font-bold text-xs">{character.armor ?? 0}</span>
                </div>
                {/* Coins: copper, silver, gold */}
                <div className="flex items-center gap-1 bg-amber-900/30 px-2 py-1 rounded-lg">
                  <span className="text-xs text-amber-700">{character.copper ?? 0}м</span>
                  <span className="text-xs text-gray-400">{character.silver ?? 0}с</span>
                  <span className="text-xs text-amber-400">{character.gold ?? 0}з</span>
                </div>
              </div>

              {/* Right: Stats, Abilities, Inventory and Dice buttons */}
              <div className="flex items-center gap-2">
                {/* Character Stats button */}
                <button
                  onClick={() => {
                    hapticFeedback('light');
                    setShowStats(!showStats);
                  }}
                  className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center hover:bg-gray-600 transition-colors border border-gray-600"
                >
                  <img
                    src="/assets/stats-scroll-64.webp"
                    alt="Stats"
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<span class="text-xl">📊</span>';
                    }}
                  />
                </button>

                {/* Abilities button */}
                <button
                  onClick={() => {
                    hapticFeedback('light');
                    setShowAbilities(true);
                  }}
                  className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center hover:bg-gray-600 transition-colors border border-gray-600"
                >
                  <img
                    src="/assets/abilities-book-64.png"
                    alt="Abilities"
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<span class="text-xl">📖</span>';
                    }}
                  />
                </button>

                {/* Inventory button */}
                <button
                  onClick={() => {
                    hapticFeedback('light');
                    setShowInventory(true);
                  }}
                  className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center hover:bg-gray-600 transition-colors border border-gray-600"
                >
                  <img
                    src="/assets/inventory_64.webp"
                    alt="Inventory"
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<span class="text-xl">👝</span>';
                    }}
                  />
                </button>

                {/* Dice button - active when roll is pending (either awaiting description or ready to roll) */}
                <button
                  onClick={() => {
                    if (pendingRoll && (inputState === 'AWAITING_ROLL' || inputState === 'AWAITING_DESCRIPTION')) {
                      hapticFeedback('medium');
                      // If still awaiting description, skip to roll
                      if (inputState === 'AWAITING_DESCRIPTION') {
                        setInputState('AWAITING_ROLL');
                      }
                      setShowDiceModal(true);
                    }
                  }}
                  disabled={!pendingRoll || (inputState !== 'AWAITING_ROLL' && inputState !== 'AWAITING_DESCRIPTION')}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all border ${
                    pendingRoll && (inputState === 'AWAITING_ROLL' || inputState === 'AWAITING_DESCRIPTION')
                      ? 'bg-amber-500 border-amber-400 animate-pulse'
                      : 'bg-gray-700 border-gray-600 opacity-50'
                  }`}
                >
                  <span className="text-xl">🎲</span>
                </button>
              </div>
            </div>
          </>
        )}
      </header>

      {/* Click outside to close menu */}
      {showMenu && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => setShowMenu(false)}
        />
      )}

      {/* Stats Panel (collapsible) */}
      <AnimatePresence>
        {showStats && character && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-gray-800/90 border-b border-gray-700"
          >
            <div className="p-3">
              <div className="grid grid-cols-6 gap-1.5 text-center">
                <div className="bg-gray-700/50 rounded-lg p-1.5">
                  <div className="text-[10px] text-gray-400 uppercase">СИЛ</div>
                  <div className="text-white font-bold text-sm">{character.strength ?? 10}</div>
                  <div className="text-amber-400 text-xs font-medium">{getModifier(character.strength ?? 10)}</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-1.5">
                  <div className="text-[10px] text-gray-400 uppercase">ЛОВ</div>
                  <div className="text-white font-bold text-sm">{character.dexterity ?? 10}</div>
                  <div className="text-amber-400 text-xs font-medium">{getModifier(character.dexterity ?? 10)}</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-1.5">
                  <div className="text-[10px] text-gray-400 uppercase">ТЕЛ</div>
                  <div className="text-white font-bold text-sm">{character.constitution ?? 10}</div>
                  <div className="text-amber-400 text-xs font-medium">{getModifier(character.constitution ?? 10)}</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-1.5">
                  <div className="text-[10px] text-gray-400 uppercase">ИНТ</div>
                  <div className="text-white font-bold text-sm">{character.intelligence ?? 10}</div>
                  <div className="text-amber-400 text-xs font-medium">{getModifier(character.intelligence ?? 10)}</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-1.5">
                  <div className="text-[10px] text-gray-400 uppercase">МУД</div>
                  <div className="text-white font-bold text-sm">{character.wisdom ?? 10}</div>
                  <div className="text-amber-400 text-xs font-medium">{getModifier(character.wisdom ?? 10)}</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-1.5">
                  <div className="text-[10px] text-gray-400 uppercase">ХАР</div>
                  <div className="text-white font-bold text-sm">{character.charisma ?? 10}</div>
                  <div className="text-amber-400 text-xs font-medium">{getModifier(character.charisma ?? 10)}</div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm End Modal */}
      <AnimatePresence>
        {showConfirmEnd && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setShowConfirmEnd(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-white mb-2">Завершить приключение?</h3>
              <p className="text-gray-400 text-sm mb-6">
                Это приключение будет отмечено как завершённое. Вы сможете найти его в истории.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmEnd(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gray-700 text-white font-medium"
                >
                  Отмена
                </button>
                <button
                  onClick={handleEndAdventure}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-medium"
                >
                  Завершить
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Restart Modal */}
      <AnimatePresence>
        {showConfirmRestart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setShowConfirmRestart(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-white mb-2">Начать заново?</h3>
              <p className="text-gray-400 text-sm mb-6">
                Текущее приключение будет завершено. Начнётся новая история.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmRestart(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gray-700 text-white font-medium"
                >
                  Отмена
                </button>
                <button
                  onClick={handleStartOver}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 text-black font-medium"
                >
                  Начать
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Toast */}
      <AnimatePresence>
        {showSaveToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-green-600 text-white rounded-lg shadow-lg"
          >
            ✓ Сохранено
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chapter Completed Toast */}
      <AnimatePresence>
        {showChapterToast && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-amber-600 text-white rounded-xl shadow-xl border border-amber-400"
          >
            <div className="text-center">
              <span className="text-2xl block mb-1">📜</span>
              <span className="font-medium">{showChapterToast}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages - scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-400">Загрузка приключения...</p>
            </div>
          </div>
        ) : error && messages.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <span className="text-4xl block mb-4">⚠️</span>
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={loadSession}
                className="px-4 py-2 bg-amber-500 text-black rounded-lg font-medium"
              >
                Повторить
              </button>
            </div>
          </div>
        ) : (
          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={message.id || index}
                ref={index === messages.length - 1 ? lastMessageRef : null}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-gray-800 text-gray-100 rounded-bl-md'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <p className="text-xs text-amber-500 mb-1 font-medium">Мастер</p>
                  )}
                  <div className="text-sm leading-relaxed prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                        em: ({ children }) => <em className="italic text-gray-300">{children}</em>,
                        ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}

        {/* Typing indicator */}
        {isSending && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </motion.div>
        )}

        {/* Roll prompt - show when roll is pending */}
        {pendingRoll && (inputState === 'AWAITING_ROLL' || inputState === 'AWAITING_DESCRIPTION') && !showDiceModal && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center"
          >
            <div className="px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-xl text-amber-400 text-sm text-center">
              <div>🎲 <strong>{pendingRoll.moveName}</strong></div>
              {pendingRoll.stat && (
                <div className="text-xs mt-1 text-gray-400">
                  Бросок: 2d6 + {pendingRoll.stat} | 10+ успех | 7-9 частично | 6- провал
                </div>
              )}
              <div className="text-xs mt-1">Нажмите на кубик или опишите действие</div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error toast */}
      <AnimatePresence>
        {error && messages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="mx-4 mb-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg"
          >
            <p className="text-red-400 text-sm">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky Footer - Input */}
      <footer className="sticky bottom-0 z-30 bg-gray-900 border-t border-gray-800 p-4">
        {inputState === 'AWAITING_ROLL' ? (
          <div className="text-center py-3 text-gray-400">
            <span className="text-2xl mr-2">🎲</span>
            Нажмите на кубик, чтобы бросить
          </div>
        ) : inputState === 'WAITING_AI' || isSending ? (
          <div className="text-center py-3 text-gray-400 flex items-center justify-center gap-2">
            <div className="animate-spin w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full" />
            <span>Ожидание ответа Мастера...</span>
          </div>
        ) : (
          <div className="flex gap-2">
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              rows={1}
              disabled={isInputDisabled}
              className="flex-1 px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none resize-none disabled:opacity-50"
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
            <button
              onClick={handleSendClick}
              disabled={!inputText.trim() || isInputDisabled}
              className="px-6 py-3 rounded-xl bg-amber-500 text-black font-medium disabled:opacity-50 disabled:bg-gray-700 disabled:text-gray-500 transition-all"
            >
              Отправить
            </button>
          </div>
        )}
      </footer>

      
      {/* Dice Roll Modal */}
      {pendingRoll && (
        <DiceRollModal
          isOpen={showDiceModal}
          rollRequest={pendingRoll}
          onClose={() => setShowDiceModal(false)}
          onResult={handleDiceResult}
        />
      )}

      {/* Last Breath Modal (death check) */}
      <LastBreathModal
        isOpen={showLastBreathModal}
        characterName={character?.name || 'Герой'}
        onRollResult={handleLastBreathResult}
      />

      {/* Inventory Modal */}
      <InventoryModal
        isOpen={showInventory}
        onClose={() => setShowInventory(false)}
        character={character}
      />

      {/* Abilities Modal */}
      <AbilitiesModal
        isOpen={showAbilities}
        onClose={() => setShowAbilities(false)}
        character={character}
      />
    </div>
  );
}
